import { useLayoutEffect } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import { observer } from 'mobx-react-lite';
import RoundButton from 'shared/buttons/RoundButton';
import Separator from 'shared/layout/Separator';
import Spacer from 'shared/layout/Spacer';
import styled from 'styled-components';

import { useMedia } from '@hooks/use-media';

import useDetailLoad from 'hooks/useDetailLoad';
import useDocumentTitle from 'hooks/useDocumentTitle';
import usePreviewCheck from 'hooks/usePreviewCheck';
import { useStore } from 'models/stores';

import BackButton from './BackButton';
import JobHeader from './JobHeader';
import LanguageDataNotAvailable from './LanguageDataNotAvailable';
import MainHeader from './MainHeader';

function JobDetail() {
  const { jobStore, uiStore, companyStore } = useStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { jobID } = useParams();
  const small = useMedia(`(max-width: 600px)`);

  uiStore.selectJob(jobID);
  const job = jobStore.items.get(jobID);
  const data = job && job.languages.find((l) => l.language === uiStore.language);
  useDocumentTitle(data && data.title);
  const company = companyStore.current.name;
  usePreviewCheck(jobID);

  useDetailLoad(jobID, jobStore, uiStore, company, () => {
    navigate(`/${company}/${uiStore.language}/notfound/`);
  });

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!job || job.state !== 'loaded') {
    return null;
  }

  const applyButton = (
    <Link to={{ pathname: 'apply', search: location.search }}>
      <RoundButton full={small}>{uiStore.t('apply_button')}</RoundButton>
    </Link>
  );
  return (
    <Wrapper>
      <MainHeader>
        {uiStore.preview ? (
          <div />
        ) : (
          <BackButton
            to={{
              pathname: `/${company}/${uiStore.language}`,
              search: location.search,
            }}
            state={location.state}
            text={uiStore.t('backToJobs')}
          />
        )}
        {data && applyButton}
      </MainHeader>
      {data ? (
        <>
          <JobHeader job={job} langData={data} showDeadline />
          <Spacer top={30} bottom={30}>
            <Separator />
          </Spacer>
          <Spacer top={30} bottom={30}>
            <Html dangerouslySetInnerHTML={{ __html: data.description }} />
          </Spacer>
          <MobileApply>{applyButton}</MobileApply>
        </>
      ) : (
        <LanguageDataNotAvailable />
      )}
    </Wrapper>
  );
}
export default observer(JobDetail);

const MobileApply = styled.div`
  display: none;
  @media (max-width: ${(p) => p.theme.bp.sm}) {
    margin-top: 50px;
    display: block;
    width: 100%;
  }
`;

const Wrapper = styled.div`
  margin: 0 auto;
  width: 100%;
  padding: 40px 0;
  max-width: 580px;
  @media (max-width: 720px) {
    padding: 40px;
  }

  @media (max-width: 460px) {
    padding: 40px 20px;
  }
`;

const Html = styled.div`
  color: ${(props) => props.theme.textColor};
  font-size: 16px;
  font-weight: normal;
  font-family: ${(props) => props.theme.fontFamily};
  line-height: 24px;
  p {
    margin: 0;
    padding: 0;
  }
  p:empty {
    padding: 11px 0;
  }
  ol,
  ul {
    margin: 4px;
  }
  > p > a {
    color: ${(props) => props.theme.blue100};
    &:hover {
      text-decoration: underline;
    }
  }
`;
